import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useAuth } from "react-oidc-context";

import { ApiError } from "../../lib/api";
import { discardHelpSession } from "../../lib/auth";
import { readSSE } from "../../lib/sse";
import { useApi } from "../../lib/useApi";
import { clearManualImages } from "./imageResources";
import { cleanInput, codepoints, redactEmail, trimHistory } from "./chatHistory";
import { useManualGeneration, useManualOwner } from "./manualHooks";
import type { HelpChatRequest, HelpDone, HelpError, HelpStatus, HelpTurn } from "./chatTypes";

export function useHelpStatus() {
  const api = useApi();
  const auth = useAuth();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const current = useRef(generation);
  current.current = generation;
  return useQuery({ queryKey: ["help-status", iss, sub],
    queryFn: async ({ signal }) => {
      const snapshot = generation;
      const result = await api.request<HelpStatus>("/api/core/help/status", { signal, cache: "no-store" });
      if (signal.aborted || current.current !== snapshot) throw new DOMException("Owner changed", "AbortError");
      return result;
    },
    enabled: auth.isAuthenticated, staleTime: 0, refetchInterval: 300_000 });
}

export type ChatEntry = { id: number; question: string; answer: string; done?: HelpDone;
  incomplete: boolean; error?: string; retryable?: boolean; retryAfter?: number; retryAt?: number; snapshot: HelpChatRequest };

export function useHelpChat(status: HelpStatus | undefined, manualView?: string) {
  const api = useApi();
  const auth = useAuth();
  const { events: authEvents, removeUser } = auth;
  const client = useQueryClient();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const owner = JSON.stringify([iss, sub, generation, status?.view, manualView]);
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const [state, setState] = useState<{ owner: string; turns: ChatEntry[]; streaming: boolean; trimmed: boolean }>(
    { owner, turns: [], streaming: false, trimmed: false });
  const controller = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  const clear = useCallback(() => {
    controller.current?.abort(); controller.current = null; sequence.current += 1;
    setState({ owner: currentOwner.current, turns: [], streaming: false, trimmed: false });
  }, []);
  useEffect(() => { clear(); }, [owner, clear]);

  useEffect(() => {
    const discard = () => {
      clear();
      const filters = { predicate: (q: { queryKey: readonly unknown[] }) =>
        ["manual", "manual-index", "help-status"].includes(String(q.queryKey[0])) && q.queryKey[1] === iss && q.queryKey[2] === sub };
      void client.cancelQueries(filters).then(() => client.removeQueries(filters));
      clearManualImages(JSON.stringify([iss, sub, generation]));
    };
    window.addEventListener("itmgmt-signout", discard);
    const unloaded = authEvents.addUserUnloaded(discard);
    const signedOut = authEvents.addUserSignedOut(discard);
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("itmgmt-auth") : null;
    if (channel) channel.onmessage = (event) => { if (event.data === "signout") {
      discardHelpSession(); void removeUser();
    } };
    return () => {
      window.removeEventListener("itmgmt-signout", discard); unloaded(); signedOut(); channel?.close();
      controller.current?.abort();
      // cancel/remove で古い要求の応答は TanStack Query に採用されない。
      const filters = { queryKey: ["help-status", iss, sub] };
      void client.cancelQueries(filters).then(() => client.removeQueries(filters));
    };
  }, [authEvents, removeUser, clear, client, generation, iss, sub]);

  useEffect(() => { void client.invalidateQueries({ queryKey: ["help-status", iss, sub] }); }, [client, generation, iss, sub]);

  const turns = state.owner === owner ? state.turns : [];
  const send = async (question: string, screen: string | null, retry?: HelpChatRequest) => {
    if (!status || controller.current || currentOwner.current !== owner) return;
    question = redactEmail(cleanInput(question));
    if (!question.trim() || codepoints(question) > 1000) return;
    const complete: HelpTurn[] = turns.filter((t) => t.done?.stop === "end" && !t.incomplete).flatMap((t) =>
      [{ role: "user" as const, text: t.question }, { role: "assistant" as const, text: t.answer }]);
    const trimmed = trimHistory(complete);
    const snapshot = retry ?? { question, history: trimmed.history, screen, view: status.view };
    const id = ++sequence.current;
    const abort = new AbortController(); controller.current = abort;
    const valid = () => currentOwner.current === owner && sequence.current === id;
    setState((s) => ({ owner, turns: [...(s.owner === owner ? s.turns : []),
      { id, question: snapshot.question, answer: "", incomplete: true, snapshot }], streaming: true, trimmed: trimmed.trimmed }));
    const update = (change: Partial<ChatEntry>) => { if (valid()) setState((s) => ({ ...s,
      turns: s.turns.map((t) => t.id === id ? { ...t, ...change } : t) })); };
    let answer = "";
    try {
      const response = await api.helpChat(snapshot, abort.signal);
      await readSSE(response, ({ event, data }) => {
        if (!valid()) return;
        if (event === "delta") {
          const text = (data as { text?: unknown }).text;
          if (typeof text !== "string" || codepoints(answer + text) > 4000) throw new Error("network");
          answer += text; update({ answer });
        } else if (event === "done") {
          const done = data as HelpDone;
          if (!["end", "max_tokens", "refusal"].includes(done.stop) || !Array.isArray(done.citations)) throw new Error("network");
          update({ done, incomplete: false });
        } else if (event === "error") {
          const error = data as HelpError;
          update({ error: error.message, retryable: error.retryable });
        }
      });
    } catch (error) {
      if (!valid()) return;
      if (error instanceof ApiError && error.reason === "view_changed") {
        clear();
        void message.info("表示できる範囲が変わったため、会話を消しました");
        void client.invalidateQueries({ queryKey: ["manual-index", iss, sub] });
        clearManualImages(JSON.stringify([iss, sub, generation]));
      } else if (abort.signal.aborted) update({ incomplete: true, error: "停止しました" });
      else update({ incomplete: true, done: undefined, error: error instanceof ApiError ? error.message : "通信が途切れました",
        retryable: !(error instanceof ApiError) || [429, 503].includes(error.status),
        retryAfter: error instanceof ApiError ? error.retryAfter : undefined,
        retryAt: error instanceof ApiError && error.retryAfter ? Date.now() + error.retryAfter * 1000 : undefined });
    } finally {
      if (valid()) { controller.current = null; setState((s) => ({ ...s, streaming: false })); }
      void client.invalidateQueries({ queryKey: ["help-status", iss, sub] });
    }
  };
  const stop = () => controller.current?.abort();
  return { turns, streaming: state.owner === owner && state.streaming, trimmed: state.trimmed, send, stop, clear };
}
